/**
 * Textos do trabalho do operador (spec 018): o motivo de cada item do Hoje e o
 * "Prazo" da lista — sempre dizendo QUAL prazo (resposta, solução, etapa), e
 * nunca inventando um: ticket sem SLA e sem prazo de etapa é "Sem SLA".
 */
import {
  SLA_LEG_LABELS,
  capitalize,
  situationLabel,
  slaTargetState,
  type SlaLeg,
  type Terms,
  type TicketListItem,
  type WorkReason,
} from '@erp/shared';

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** "menos de 1 min", "12 min", "3h", "3h 20min", "2d", "2d 5h" */
export function formatElapsed(ms: number): string {
  const abs = Math.max(0, ms);
  if (abs < MIN) return 'menos de 1 min';
  if (abs < HOUR) return `${Math.floor(abs / MIN)} min`;
  if (abs < DAY) {
    const h = Math.floor(abs / HOUR);
    const m = Math.floor((abs % HOUR) / MIN);
    return m === 0 ? `${h}h` : `${h}h ${m}min`;
  }
  const d = Math.floor(abs / DAY);
  const h = Math.floor((abs % DAY) / HOUR);
  return h === 0 ? `${d}d` : `${d}d ${h}h`;
}

const ms = (d: Date | string) => new Date(d).getTime();

/** "Por que está aqui" — a frase do motivo, no vocabulário do tenant. */
export function reasonText(reason: WorkReason, terms: Terms, now: Date): string {
  const t = now.getTime();
  switch (reason.kind) {
    case 'sla_overdue':
      return `Prazo de ${SLA_LEG_LABELS[reason.leg]} vencido há ${formatElapsed(t - ms(reason.dueAt))}`;
    case 'needs_reply':
      return `Cliente aguarda resposta há ${formatElapsed(t - ms(reason.since))}`;
    case 'stuck':
      return `Parado em ${reason.stageName} há ${formatElapsed(t - ms(reason.since))}`;
    case 'sla_due_soon':
      return `Prazo de ${SLA_LEG_LABELS[reason.leg]} vence em ${formatElapsed(ms(reason.dueAt) - t)}`;
    case 'unassigned':
      return `Sem responsável há ${formatElapsed(t - ms(reason.since))}`;
    case 'waiting_client':
      return `${situationLabel(terms, reason.situation)} há ${formatElapsed(t - ms(reason.since))}`;
  }
}

/** Tom do motivo (cor do marcador): vencido/parado em vermelho, espera em âmbar… */
export function reasonTone(reason: WorkReason): 'danger' | 'warning' | 'info' | 'muted' {
  switch (reason.kind) {
    case 'sla_overdue':
    case 'stuck':
      return 'danger';
    case 'needs_reply':
    case 'sla_due_soon':
      return 'warning';
    case 'unassigned':
      return 'info';
    case 'waiting_client':
      return 'muted';
  }
}

export interface DueInfo {
  text: string;
  tone: 'danger' | 'warning' | 'muted' | 'ok';
  /** o tipo, para leitor de tela e tooltip */
  kind: 'sla' | 'stage' | 'none' | 'done';
}

/**
 * Coluna "Prazo" da lista (US2 AS3): o SLA quando existe, dizendo se é de
 * resposta ou de solução; sem SLA, "Parado há X" quando o ticket passou do
 * prazo da etapa (017, FR-008), senão "Sem SLA". Nunca um prazo inventado.
 */
export function dueInfo(
  t: Pick<
    TicketListItem,
    | 'attendDueAt'
    | 'solveDueAt'
    | 'attendedAt'
    | 'slaPausedAt'
    | 'createdAt'
    | 'closedAt'
    | 'canceledAt'
    | 'stuckInStage'
    | 'stageEnteredAt'
  >,
  now: Date
): DueInfo {
  if (t.closedAt || t.canceledAt) return { text: '—', tone: 'muted', kind: 'done' };
  if (t.slaPausedAt && (t.attendDueAt || t.solveDueAt)) return { text: 'SLA pausado', tone: 'muted', kind: 'sla' };

  // o alvo ATIVO mais urgente: resposta enquanto não houve, depois a solução
  const legs: Array<{ leg: SlaLeg; dueAt: Date | string | null; doneAt: Date | string | null }> = [
    { leg: 'attend', dueAt: t.attendDueAt, doneAt: t.attendedAt },
    { leg: 'solve', dueAt: t.solveDueAt, doneAt: null },
  ];
  const active = legs
    .map((l) => ({ ...l, state: slaTargetState({ dueAt: l.dueAt, doneAt: l.doneAt, pausedAt: null, startAt: t.createdAt, now }) }))
    .filter((l) => l.state !== null && l.state !== 'met' && l.dueAt !== null)
    .sort((a, b) => ms(a.dueAt!) - ms(b.dueAt!));
  const next = active[0];
  if (next && next.dueAt) {
    const label = capitalize(SLA_LEG_LABELS[next.leg]);
    const diff = ms(next.dueAt) - now.getTime();
    if (next.state === 'overdue') return { text: `${label}: vencido há ${formatElapsed(-diff)}`, tone: 'danger', kind: 'sla' };
    return {
      text: `${label}: vence em ${formatElapsed(diff)}`,
      tone: next.state === 'due_soon' ? 'warning' : 'ok',
      kind: 'sla',
    };
  }

  if (t.stuckInStage) {
    return { text: `Parado há ${formatElapsed(now.getTime() - ms(t.stageEnteredAt))}`, tone: 'danger', kind: 'stage' };
  }
  return { text: 'Sem SLA', tone: 'muted', kind: 'none' };
}

export const TONE_TEXT: Record<DueInfo['tone'] | 'info', string> = {
  danger: 'text-[#CF1322]',
  warning: 'text-[#D48806]',
  info: 'text-[#1677FF]',
  muted: 'text-muted-foreground',
  ok: 'text-foreground',
};
